import { StateMessage } from '@/components/ui/controls';
import { Card, Field } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import StatTile from '@/components/ui/stat-tile';
import { table } from '@/components/ui/styles';
import { formatDate } from '@/utils/format';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import type { ReportCampaigns } from './api-types';
import HBarChart from './hbar-chart';
import ReportsHeader from './reports-nav';
import { percent } from './series';
import TableCard from './table-card';

/** A campaign's row key: the same campaign name can run on several sources or media. */
const campaignKey = (c: { campaign: string; source: string; medium: string }) => `${c.campaign}|${c.source}|${c.medium}`;

const COLUMNS: SortColumns<ReportCampaigns['campaigns'][number]> = {
  campaign: { value: (c) => c.campaign },
  source: { value: (c) => `${c.source} / ${c.medium}` },
  leads: { value: (c) => c.leads, desc: true },
  sales: { value: (c) => c.sales, desc: true },
  rate: { value: (c) => (c.leads ? c.sales / c.leads : undefined), desc: true },
  first: { value: (c) => dateValue(c.first_lead), desc: true },
};

/**
 * Reports, Campaigns (ATT-3, ATT-5): the UTM campaigns leads first came through, and how
 * many of them became a sale (GET /reports/campaigns). On a phone the table keeps the
 * campaign, its leads and its rate, with the rest folded under the name (UI-1).
 */
const CampaignsReportPage = () => {
  const list = useApi<ReportCampaigns>('/reports/campaigns');
  const campaigns = list.data?.campaigns ?? [];
  const { rows, sort, toggle } = useSort(campaigns, COLUMNS);
  const leads = campaigns.reduce((n, c) => n + c.leads, 0);
  const sales = campaigns.reduce((n, c) => n + c.sales, 0);

  if (list.error && !list.data) {
    return (
      <div>
        <ReportsHeader />
        <StateMessage title="Campaigns could not be loaded" tone="error">
          {list.error}
        </StateMessage>
      </div>
    );
  }

  return (
    <div>
      <ReportsHeader />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatTile label="Campaigns" value={String(campaigns.length)} note="With at least one lead" />
        <StatTile label="Leads from campaigns" value={String(leads)} note="By first touch" />
        <StatTile label="Sales" value={String(sales)} note="A casino is live" />
        <StatTile label="Lead to sale" value={percent(sales, leads)} note={`${sales} of ${leads} leads`} />
      </div>

      {list.data && campaigns.length === 0 ? (
        <div className="mb-6">
          <StateMessage title="No campaigns yet">Campaigns appear once a lead&rsquo;s first touch carries a utm_campaign.</StateMessage>
        </div>
      ) : (
        <div className="mb-6 grid grid-cols-1 gap-6 2xl:grid-cols-[2fr_3fr]">
          <Card title="Leads by campaign">
            <HBarChart
              rows={campaigns.map((c) => ({ key: campaignKey(c), label: c.campaign, value: c.leads }))}
              format={String}
              label="Leads by first-touch campaign"
              tooltip={(r) => {
                const c = campaigns.find((x) => campaignKey(x) === r.key);
                return (
                  <>
                    <span className="font-semibold text-fg-strong">{r.value}</span>
                    <span className="ml-1.5 text-fg-muted">
                      leads, {c?.sales} sales ({percent(c?.sales ?? 0, r.value)})
                    </span>
                  </>
                );
              }}
            />
          </Card>
          <TableCard title="Campaigns">
            <thead className={table.head}>
              <tr>
                <SortTh label="Campaign" column="campaign" sort={sort} onSort={toggle} />
                <SortTh label="Source / medium" column="source" sort={sort} onSort={toggle} className="hidden md:table-cell" />
                <SortTh label="Leads" column="leads" sort={sort} onSort={toggle} right />
                <SortTh label="Sales" column="sales" sort={sort} onSort={toggle} right className="hidden md:table-cell" />
                <SortTh label="Lead to sale" column="rate" sort={sort} onSort={toggle} right />
                <SortTh label="First lead" column="first" sort={sort} onSort={toggle} className="hidden md:table-cell" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={campaignKey(c)} className={table.row}>
                  <td className={table.td}>
                    <span className="font-mono text-xs text-fg-strong [overflow-wrap:anywhere]">{c.campaign}</span>
                    <span className="mt-0.5 block text-xs text-fg-muted md:hidden">
                      {c.source} / {c.medium} · {c.sales} {c.sales === 1 ? 'sale' : 'sales'} · since {formatDate(c.first_lead)}
                    </span>
                  </td>
                  <td className={`${table.td} hidden whitespace-nowrap text-fg-muted md:table-cell`}>
                    {c.source} / {c.medium}
                  </td>
                  <td className={`${table.td} text-right tabular-nums`}>{c.leads}</td>
                  <td className={`${table.td} hidden text-right tabular-nums md:table-cell`}>{c.sales}</td>
                  <td className={`${table.td} text-right tabular-nums text-fg-muted`}>{percent(c.sales, c.leads)}</td>
                  <td className={`${table.td} hidden whitespace-nowrap tabular-nums text-fg-muted md:table-cell`}>{formatDate(c.first_lead)}</td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        </div>
      )}

      <Card title="How a lead gets its campaign" className="max-w-3xl">
        <dl>
          <Field label="UTM tags">
            Every campaign URL carries utm_source, utm_medium and utm_campaign. They are stored with the registration or the contact form (ATT-3).
          </Field>
          <Field label="First touch">
            A cookie keeps the first source when a visitor comes back later, so a lead counts for the campaign that brought them first (ATT-5).
          </Field>
          <Field label="AI assistants">ChatGPT and the like send no UTM tags. They are recognised from the referrer (ATT-4) and show under Sources.</Field>
        </dl>
      </Card>
    </div>
  );
};

export default CampaignsReportPage;
